// src/components/ads/TripAd.jsx
// A sponsored DMC trip in an ad slot, labelled "Sponsored" the way search ads are.
//   <TripAd placement="home" />                          wide banner
//   <TripAd placement="route_results" variant="inline" ctx={{ destination }} />   compact, between results
// Nothing renders when ads are off (FEATURES.ENABLE_TRIP_ADS) or the slot is off (TRIP_AD_PLACEMENTS).
import React, { useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { pickAd, adHref, adEvent } from "../../ads/tripAds";

const inr = (n) => `₹${Math.round(n || 0).toLocaleString("en-IN")}`;

export default function TripAd({ placement, variant = "banner", ctx = {}, style = null }) {
    const ad = useMemo(() => pickAd(placement, ctx), [placement, ctx.destination, ctx.exclude]); // eslint-disable-line react-hooks/exhaustive-deps
    const ref = useRef(null);
    useEffect(() => {
        if (!ad || !ref.current || typeof IntersectionObserver === "undefined") return undefined;
        const io = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting)) { adEvent("impression", ad, placement); io.disconnect(); } }, { threshold: 0.5 });
        io.observe(ref.current);
        return () => io.disconnect();
    }, [ad, placement]);
    if (!ad) return null;
    const inline = variant === "inline";
    return (
        <aside ref={ref} aria-label="Sponsored trip" className={`rc-trip-ad rc-trip-ad--${variant}`} style={{ ...S.wrap, ...(inline ? S.wrapInline : null), ...style }}>
            <style>{CSS}</style>
            <Link to={adHref(ad, placement)} onClick={() => adEvent("click", ad, placement)} style={{ ...S.link, ...(inline ? S.linkInline : null) }} className="rc-ta-link">
                {ad.image && <img src={ad.image} alt={ad.image_alt} loading="lazy" style={{ ...S.img, ...(inline ? S.imgInline : null), objectPosition: ad.image_focus }} className="rc-ta-img" />}
                <div style={S.body}>
                    <div style={S.top}>
                        <span style={S.sponsored}>Sponsored</span>
                        {ad.badge && <span style={S.badge}>{ad.badge}</span>}
                    </div>
                    <div style={{ ...S.head, ...(inline ? { fontSize: 16 } : null) }}>{ad.headline}<span style={S.by}> · by {ad.by}</span></div>
                    <div style={S.sub} className="rc-ta-sub">{ad.sub}</div>
                    <div style={S.foot}>
                        <span><span style={S.from}>from</span> <b style={S.price}>{inr(ad.price_pp)}</b><span style={S.from}> per person · {ad.nights}N/{ad.days}D</span></span>
                        <span style={S.cta} className="rc-ta-cta">See the trip →</span>
                    </div>
                </div>
            </Link>
        </aside>
    );
}

const S = {
    wrap: { width: "100%", maxWidth: 900, margin: "24px auto", boxSizing: "border-box", fontFamily: "'Inter', -apple-system, system-ui, sans-serif", textAlign: "left" },
    wrapInline: { maxWidth: "none", margin: 0 },
    link: { display: "flex", alignItems: "stretch", gap: 0, textDecoration: "none", color: "#0F172A", background: "#fff", border: "1px solid #E2E8F0", borderRadius: 16, overflow: "hidden", boxShadow: "0 1px 2px rgba(15,23,42,.04)" },
    linkInline: { borderRadius: 14, borderStyle: "dashed", borderColor: "#CBD5E1" },
    img: { width: 260, minHeight: 150, objectFit: "cover", flexShrink: 0, display: "block" },
    imgInline: { width: 170, minHeight: 120 },
    body: { flex: 1, minWidth: 0, padding: "14px 16px", display: "flex", flexDirection: "column", gap: 4 },
    top: { display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" },
    sponsored: { fontSize: 11, fontWeight: 800, color: "#475569", border: "1px solid #CBD5E1", borderRadius: 4, padding: "0 5px", lineHeight: "17px" },
    badge: { fontSize: 11, fontWeight: 700, color: "#047857", background: "#ECFDF5", borderRadius: 99, padding: "1px 8px" },
    head: { fontWeight: 800, fontSize: 18, lineHeight: 1.25, marginTop: 2 },
    by: { fontWeight: 600, fontSize: 13, color: "#64748B" },
    sub: { fontSize: 13.5, color: "#475569", lineHeight: 1.45 },
    foot: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap", marginTop: "auto", paddingTop: 6 },
    from: { fontSize: 12.5, color: "#64748B" },
    price: { fontSize: 17, color: "#0F172A" },
    cta: { fontWeight: 800, fontSize: 13.5, color: "#fff", background: "#0E7490", borderRadius: 10, padding: "8px 12px", whiteSpace: "nowrap" },
};
const CSS = `
.rc-trip-ad .rc-ta-link:hover{border-color:#0E7490}
.rc-trip-ad .rc-ta-link:hover .rc-ta-cta{background:#0B5F75}
@media (max-width: 640px){
  .rc-trip-ad .rc-ta-link{flex-direction:column}
  .rc-trip-ad .rc-ta-img{width:100% !important;height:140px;min-height:0 !important}
  .rc-trip-ad .rc-ta-sub{display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
}`;
